import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '@canaveral/components';
import { injectIntl } from 'react-intl';
import get from 'lodash/get';
import { Alert } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../src/shared/i18n';
import ThemedButton from '../../../../stateless/ThemedButton';
import Translation from '../../../../stateless/Translation';
import CreateLeadForm from './CreateLeadForm';
import styles from './CreateLeadModal.scss';
import googleSessionID from '../../../../../lib/googleSessionID';
import { getClientId } from '../../../../../../src/web/utils/googleAnalytics';

const i18nPrefix = 'widgets.unitstable.leadform.';
const createTranslation = (suffix, defaultMessage, values = {}) => (
  <Translation
    id={`${i18nPrefix}${suffix}`}
    defaultMessage={defaultMessage}
    values={values}
  />
);

const propTypes = {
  show: PropTypes.bool.isRequired,
  unitGroup: PropTypes.shape({
    size: PropTypes.string,
    width: PropTypes.number,
    length: PropTypes.number,
    type: PropTypes.string,
    price: PropTypes.number,
    discountedPrice: PropTypes.number,
    discountedPriceWeekly: PropTypes.number,
    priceWeekly: PropTypes.number,
  }),
  onSubmit: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
  confirmed: PropTypes.bool,
  theme: PropTypes.shape({
    fonts: PropTypes.shape({
      primary: PropTypes.shape({
        name: PropTypes.string,
        href: PropTypes.string,
      }),
      secondary: PropTypes.shape({
        name: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    elements: PropTypes.shape({
      button: PropTypes.object,
    }),
  }),
  facility: PropTypes.shape({
    currency: PropTypes.string.isRequired,
    currencySymbol: PropTypes.string.isRequired,
    address: PropTypes.shape({
      state: PropTypes.string.isRequired,
    }),
    settings: PropTypes.shape({
      useWeeklyRates: PropTypes.bool.isRequired,
    }).isRequired,
  }),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  ipAddress: PropTypes.string,
  errorMessage: PropTypes.string,
  saving: PropTypes.bool.isRequired,
};

const defaultProps = {
  unitGroup: {
    size: '8\'x33\'',
    width: 8,
    length: 33,
    type: 'Parking',
    price: 86.99,
    discountedPrice: 74.99,
    discountedPriceWeekly: 17.32,
    priceWeekly: 20.09,
  },
  confirmed: false,
  facility: {
    currencySymbol: '$',
    address: {
      state: null,
    },
  },
  theme: {
    fonts: {
      primary: {},
      secondary: {},
    },
    elements: {},
  },
  ipAddress: undefined,
  errorMessage: undefined,
};

class CreateLeadModal extends React.Component {
  state = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    countryCode: '',
    email: '',
    comments: '',
    formError: false,
    formSubmitted: false,
  }

  render() {
    if (!get(this.props, 'facility.address')) {
      return null;
    }

    const {
      unitGroup,
      theme,
      intl: { formatMessage },
      facility: { currencySymbol, address: { state }, settings: { useWeeklyRates } },
      onSubmit,
      confirmed,
      ipAddress,
      show,
      onDismiss,
      errorMessage,
      saving,
    } = this.props;
    const {
      type,
      price,
      discountedPrice,
      discountedPriceWeekly,
      priceWeekly,
      width,
      length,
      size,
      id: unitGroupId,
    } = unitGroup;
    const { elements: { button }, fonts: { primary, secondary } } = theme;
    const weeklyRate = (discountedPriceWeekly || priceWeekly);
    const monthlyRate = (discountedPrice || price);
    const unitPrice = useWeeklyRates ? weeklyRate : monthlyRate;

    const translate = (suffix, defaultMessage, values) => (
      formatMessage({
        id: `${i18nPrefix.toLowerCase()}${suffix.toLowerCase()}`,
        defaultMessage,
      }, values)
    );
    const unitType = /parking/i.test(type)
      ? translate('parkingSpace', 'Parking Space')
      : translate('workshopUnit', 'Workshop Unit');

    const onChange = (e) => {
      this.setState({
        [e.target.name]: e.target.value,
      });
    };

    const updatePhone = (_, phone, { dialCode }) => {
      onChange({ target: { value: phone, name: 'phoneNumber' } });
      onChange({ target: { value: dialCode, name: 'countryCode' } });
    };

    const handleSubmit = () => {
      const {
        firstName,
        lastName,
        phoneNumber,
        email,
      } = this.state;

      const payload = {
        firstName,
        lastName,
        phoneNumber,
        email,
        unitGroupId,
        ipAddress,
        googleClientId: getClientId(),
      };

      if (!firstName.length || !lastName.length || !phoneNumber.length || !email.length) {
        this.setState({
          formError: true,
        });
        return;
      }

      const ga4SessionID = googleSessionID();
      if (ga4SessionID) {
        payload.googleSessionId = ga4SessionID;
      }

      onSubmit(payload);
    };

    const unitName = () => {
      const isParking = /parking/i.test(type);
      const sizeUnits = (
        formatMessage({
          id: 'widgets.unitstable.storagemart.sizeunits',
          defaultMessage: 'ft',
        })
      );

      if (isParking) {
        return (
          <h4
            style={{ fontFamily: secondary.name }}
          >
            {createTranslation(
              'parkingDesc',
              'Fits vehicles up to {width} {sizeUnits} W x {length} {sizeUnits} L',
              { width, length, sizeUnits },
            )}
          </h4>
        );
      }
      return (
        <h4 style={{ fontFamily: secondary.name }}>
          {createTranslation(
            'workshopDesc',
            '{size} Workshop Unit',
            { size },
          )}
        </h4>
      );
    };

    const unitSummary = (
      <div className={styles.createLeadSummaryContainer}>
        <h5 style={{ fontFamily: primary.name }}>
          {createTranslation(
            'sizeFeatures',
            'Unit Sizes/Features',
          )}
        </h5>
        <div className={styles.createLeadUnitSummary}>
          <div className={styles.unitName}>{unitName()}</div>
          <div className={styles.unitPrices} style={{ fontFamily: primary.name }}>
            <div className={`${styles.discountDescriptiveOnlineOnly}`}>
              <span
                className={styles.discountDescriptiveNumberOnlineOnly}
              >
                {formatCurrency({
                  price: unitPrice,
                  decimals: 2,
                  currencySymbol,
                  state,
                })}
              </span>
              <sup>
                {useWeeklyRates
                  ? <Translation id="widgets.abbreviation.wk" defaultMessage="/wk" />
                  : <Translation id="widgets.abbreviation.mo" defaultMessage="/mo" />
                }
              </sup>
            </div>
            <div className={`${styles.weeklyPricing}`}>
              <Translation id="widgets.abbreviation.billedmonth" defaultMessage="Billed Monthly" />
            </div>
          </div>
        </div>
      </div>
    );

    const createLeadModalDescription = (
      <p className={styles.createLeadModalDescription} style={{ fontFamily: secondary.name }}>
        {confirmed
          ? createTranslation(
            'confirmation',
            'Thank you! We\'ve received your inquiry and a storage expert will be in touch soon.',
          )
          : createTranslation(
            'topMessage',
            `Thank you for your interest in reserving a workshop or parking unit at StorageMart.
          After you complete this form, one of our storage experts will contact you to finish the rental process.`,
          )
        }
      </p>
    );

    const modalBody = (
      <div className={styles.createLeadBody}>
        {createLeadModalDescription}
        {unitSummary}
        {errorMessage ? (
          <Alert bsStyle="danger">
            {createTranslation(
              'formErrorMessage',
              'There was an issue submitting the form. Please try again',
            )}
          </Alert>
        ) : null}
        {!confirmed
          && (
            <CreateLeadForm
              handleSubmit={onSubmit}
              fieldFont={primary.name}
              onChange={onChange}
              updatePhone={updatePhone}
              {...this.state}
            />
          )
        }
      </div>
    );

    const footerDismiss = !confirmed
      ? { style: { color: '#4a4a4a', fontWeight: '300' } }
      : { style: { fontWeight: 700, color: '#4a4a4a', ...button } };


    return (
      <Modal
        show={show}
        onDismiss={!saving ? onDismiss : null}
        aria-labelledby="create-lead-modal"
        size="md"
        className={styles.createLeadModal}
      >
        <Modal.Header className={styles.createLeadHeader}>
          <Modal.Title>
            <h3 style={{ fontFamily: secondary.name }} id="create-lead-modal">
              {createTranslation(
                'header',
                'Reserve a {unitType}',
                { unitType },
              )}
            </h3>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalBody}
        </Modal.Body>
        <Modal.Footer className={styles.createLeadFooter}>
          <div className={styles.footerButtonGroup} style={{ fontFamily: secondary.name }}>
            <ThemedButton
              onClick={onDismiss}
              context="link"
              {...footerDismiss}
              disabled={saving}
            >
              {confirmed
                ? createTranslation(
                  'close',
                  'Close',
                )
                : createTranslation(
                  'cancel',
                  'Cancel',
                )
              }
            </ThemedButton>
            {!confirmed
              && (
                <ThemedButton
                  onClick={handleSubmit}
                  style={{ ...button, fontWeight: 700, color: '#4a4a4a' }}
                  disabled={saving}
                >
                  {createTranslation(
                    'reserve',
                    'Reserve',
                  )}
                </ThemedButton>
              )
            }
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

CreateLeadModal.propTypes = propTypes;
CreateLeadModal.defaultProps = defaultProps;
export default injectIntl(CreateLeadModal);
